import { useAppConfig, useHasRole, useSaveAppConfig } from '../api/hooks';
import { PageHeader } from '../app/Shell';
import { Badge, Card, JsonDocEditor, KeyValues, QueryView, parseJsonObject, unknownKeys, type JsonCheck } from '../ui';

/** Top-level keys the server's AppConfig schema knows; anything else is refused on save. */
const KNOWN_KEYS = ['revision', 'tabs', 'home', 'heroes', 'explore', 'backdrops', 'notices', 'copy', 'webShopEntry', 'features', 'minAppVersion'];

function check(text: string): JsonCheck {
  const r = parseJsonObject(text);
  const c = r.parsed;
  if (!c) return r;
  const warnings = unknownKeys(c, KNOWN_KEYS);
  if (c['webShopEntry'] !== undefined && typeof c['webShopEntry'] !== 'boolean') warnings.push('webShopEntry 只可以是 true 或 false。');
  const min = c['minAppVersion'];
  if (min !== undefined && min !== null && (typeof min !== 'string' || !/^\d+\.\d+\.\d+$/.test(min))) warnings.push('minAppVersion 要寫成 1.2.3 或 null。');
  return { ...r, warnings };
}

/**
 * Live app config: home section order, tab order, page art, the town map, notices and copy fixes.
 * The app picks a save up within about five minutes, with no store release. Where the app sells is
 * fixed, not a switch: iOS through App Store purchases, Android through the website.
 * The server validates the whole document; a bad save is refused and nothing changes.
 */
export function AppConfigPage() {
  const config = useAppConfig();
  const save = useSaveAppConfig();
  const isOwner = useHasRole('owner');

  return (
    <>
      <PageHeader title="App 設定" description="不用送審就能改的 App 內容：首頁次序、分頁、背景圖、地圖、通告和文案修正。圖片填 asset key（例如 assets/ui/town_map.webp）。只限 owner 儲存，並記錄在審計日誌。" />
      <QueryView query={config} loading="載入設定…">
        {(cur) => (
          <JsonDocEditor
            doc={cur}
            label="App 設定 JSON"
            check={check}
            canSave={isOwner}
            cantSaveWhy="需要擁有者角色"
            save={(doc, note) => save.mutateAsync({ config: doc, note })}
            saving={save.isPending}
            saveError={save.isError ? save.error : null}
            savedNote={(rev) => `已儲存為 revision ${rev}，App 大約五分鐘內更新。`}
            aside={(c) => (
              <Card title="網站與版本" sub="由上面的 JSON 讀出">
                {c.parsed ? (
                  <KeyValues
                    items={[
                      ['webShopEntry', <Badge tone={c.parsed['webShopEntry'] === true ? 'warn' : 'neutral'}>{String(c.parsed['webShopEntry'] ?? false)}</Badge>],
                      ['minAppVersion', String(c.parsed['minAppVersion'] ?? '—')],
                    ]}
                  />
                ) : (
                  <p className="faint">JSON 有錯，未能讀出。</p>
                )}
                <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
                  App 內購買固定開啟：iOS 用 App Store 內購，Android 開網站購買（Stripe）。
                </p>
                <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
                  webShopEntry：<code>true</code> = 網站導覽、首頁、功能頁和帳戶頁都有商店入口；<code>false</code> = 網站不顯示任何商店入口，但 App 跳過去的購買連結照樣可用。
                </p>
              </Card>
            )}
          />
        )}
      </QueryView>
    </>
  );
}
